import React, { useEffect, useMemo, useState } from "react";
import {
  downloadUserReport,
  downloadTeamReport,
  downloadOrgReport,
  downloadAssessmentStatusReport,
  downloadUserReportExcel,
  downloadTeamReportExcel,
  downloadOrgReportExcel,
  downloadAssessmentStatusReportExcel,
  saveBlobAs,
  getAllEmployees,
  getLeaders,
  getDashboardSummary,
} from "../api/client";
import { useAuth } from "../context/AuthContext";

/**
 * Reports — PDF download hub for leaders & admins.
 *
 * Four report types:
 *   1. Individual — one employee's full attempt history (admin only)
 *   2. Team      — one leader's team with assessed/not-assessed split
 *   3. Organization — company-wide stats, optionally filtered by business unit
 *   4. Assessment status — follow-up roster (assessed vs not-assessed) by
 *                          leader or business unit — ideal for chasing up
 *                          employees who haven't taken the quiz yet.
 *
 * All styling uses theme variables via shared .aiq-* classes in index.css.
 */
export default function Reports() {
  const { user } = useAuth();
  const isAdmin = !!(user?.is_admin || user?.role === "admin");
  const isLeadership = isAdmin || ["c_level", "manager"].includes(user?.role);

  const [employees, setEmployees] = useState([]);
  const [leaders, setLeaders] = useState([]);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [loading, setLoading] = useState(true);

  // Form state per report
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [selectedLeader, setSelectedLeader] = useState("");
  const [selectedBU, setSelectedBU] = useState("");
  const [statusLeader, setStatusLeader] = useState("");
  const [statusBU, setStatusBU] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [busy, setBusy] = useState(null); // which report is being generated

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const calls = [getLeaders(1, 10)];
        if (isAdmin) calls.push(getAllEmployees());
        if (isLeadership) calls.push(getDashboardSummary());
        const [leadersRes, ...rest] = await Promise.all(calls);
        setLeaders(leadersRes.data || []);
        let idx = 0;
        if (isAdmin) {
          setEmployees(rest[idx]?.data || []);
          idx += 1;
        }
        if (isLeadership) {
          const summary = rest[idx]?.data;
          setBusinessUnits(
            (summary?.departments || []).map((d) => d.business_unit).sort()
          );
        }
      } catch (e) {
        console.error("Failed to load report options", e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [isAdmin, isLeadership]);

  const download = async (key, fn, fallbackName) => {
    if (busy) return;
    setBusy(key);
    try {
      const res = await fn();
      saveBlobAs(res, fallbackName);
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to generate report");
    } finally {
      setBusy(null);
    }
  };

  const employeeOptions = useMemo(
    () =>
      employees
        .filter((e) => e.user_id)
        .sort((a, b) => (a.name || "").localeCompare(b.name || "")),
    [employees]
  );

  if (loading) return <div style={{ padding: 24 }} className="aiq-muted">Loading report options…</div>;

  return (
    <div style={{ padding: 24, maxWidth: 1000, margin: "0 auto" }}>
      <h1 style={{ marginTop: 0 }}>Reports</h1>
      <p className="aiq-muted" style={{ marginTop: -4 }}>
        Download reports as PDF (summary) or Excel (full roster with every
        employee field — ERP ID, personal no, designation, business unit,
        department, hierarchy, GL, phone, location, reports-to). Every
        percentage uses each session's stored total — 45 for new assessments,
        60 for legacy attempts — so old and new attempts are compared fairly.
      </p>

      <div className="aiq-cards-grid" style={{ marginTop: 16 }}>
        {/* Individual report */}
        {isAdmin && (
          <Card title="Individual user history">
            <p className="aiq-muted" style={smallText}>
              Full attempt history for one user — score trend, belts,
              complexity over time.
            </p>
            <label htmlFor="rep-emp" className="aiq-label">Employee</label>
            <select
              id="rep-emp"
              className="aiq-select"
              value={selectedEmployee}
              onChange={(e) => setSelectedEmployee(e.target.value)}
            >
              <option value="">Select an employee…</option>
              {employeeOptions.map((e) => (
                <option key={e.user_id} value={e.user_id}>
                  {e.name} — {e.email}
                </option>
              ))}
            </select>
            <ButtonRow>
              <button
                type="button"
                className="aiq-btn-primary"
                disabled={!selectedEmployee || busy === "user"}
                onClick={() =>
                  download(
                    "user",
                    () => downloadUserReport(selectedEmployee),
                    `user-${selectedEmployee}-history.pdf`
                  )
                }
              >
                {busy === "user" ? "Generating…" : "Download PDF"}
              </button>
              <button
                type="button"
                className="aiq-btn-ghost"
                disabled={!selectedEmployee || busy === "user-xlsx"}
                onClick={() =>
                  download(
                    "user-xlsx",
                    () => downloadUserReportExcel(selectedEmployee),
                    `user-${selectedEmployee}-history.xlsx`
                  )
                }
              >
                {busy === "user-xlsx" ? "Generating…" : "Download Excel"}
              </button>
            </ButtonRow>
          </Card>
        )}

        {/* Team report */}
        <Card title="Team report">
          <p className="aiq-muted" style={smallText}>
            A leader's direct reports — split between assessed and
            not-assessed, with scores and belts.
          </p>
          <label htmlFor="rep-leader" className="aiq-label">Leader</label>
          <select
            id="rep-leader"
            className="aiq-select"
            value={selectedLeader}
            onChange={(e) => setSelectedLeader(e.target.value)}
          >
            <option value="">Select a leader…</option>
            {leaders.map((l) => (
              <option key={l.employee_id} value={l.employee_id}>
                {l.name} — {l.designation || "—"} ({l.direct_report_count}{" "}
                reports)
              </option>
            ))}
          </select>
          <ButtonRow>
            <button
              type="button"
              className="aiq-btn-primary"
              disabled={!selectedLeader || busy === "team"}
              onClick={() =>
                download(
                  "team",
                  () => downloadTeamReport(selectedLeader),
                  `team-${selectedLeader}.pdf`
                )
              }
            >
              {busy === "team" ? "Generating…" : "Download PDF"}
            </button>
            <button
              type="button"
              className="aiq-btn-ghost"
              disabled={!selectedLeader || busy === "team-xlsx"}
              onClick={() =>
                download(
                  "team-xlsx",
                  () => downloadTeamReportExcel(selectedLeader),
                  `team-${selectedLeader}.xlsx`
                )
              }
            >
              {busy === "team-xlsx" ? "Generating…" : "Download Excel"}
            </button>
          </ButtonRow>
        </Card>

        {/* Organization report */}
        {isLeadership && (
          <Card title="Organization report">
            <p className="aiq-muted" style={smallText}>
              Company-wide stats: headcount, coverage, department breakdown,
              belt distribution, full assessed roster.
            </p>
            <label htmlFor="rep-bu" className="aiq-label">
              Business unit (optional)
            </label>
            <select
              id="rep-bu"
              className="aiq-select"
              value={selectedBU}
              onChange={(e) => setSelectedBU(e.target.value)}
            >
              <option value="">All departments</option>
              {businessUnits.map((bu) => (
                <option key={bu} value={bu}>
                  {bu}
                </option>
              ))}
            </select>
            <ButtonRow>
              <button
                type="button"
                className="aiq-btn-primary"
                disabled={busy === "org"}
                onClick={() =>
                  download(
                    "org",
                    () => downloadOrgReport(selectedBU || null),
                    `organization-${selectedBU || "all"}.pdf`
                  )
                }
              >
                {busy === "org" ? "Generating…" : "Download PDF"}
              </button>
              <button
                type="button"
                className="aiq-btn-ghost"
                disabled={busy === "org-xlsx"}
                onClick={() =>
                  download(
                    "org-xlsx",
                    () => downloadOrgReportExcel(selectedBU || null),
                    `organization-${selectedBU || "all"}.xlsx`
                  )
                }
              >
                {busy === "org-xlsx" ? "Generating…" : "Download Excel"}
              </button>
            </ButtonRow>
          </Card>
        )}

        {/* Assessment status report */}
        {isLeadership && (
          <Card title="Assessment status (assessed / not assessed)">
            <p className="aiq-muted" style={smallText}>
              Follow-up roster. Filter by leader or business unit, choose
              whether to include everyone, only assessed, or only not-assessed.
            </p>

            <label htmlFor="st-leader" className="aiq-label">Leader (optional)</label>
            <select
              id="st-leader"
              className="aiq-select"
              value={statusLeader}
              onChange={(e) => {
                setStatusLeader(e.target.value);
                if (e.target.value) setStatusBU("");
              }}
            >
              <option value="">— any —</option>
              {leaders.map((l) => (
                <option key={l.employee_id} value={l.employee_id}>
                  {l.name} ({l.direct_report_count} reports)
                </option>
              ))}
            </select>

            <label htmlFor="st-bu" className="aiq-label">Business unit (optional)</label>
            <select
              id="st-bu"
              className="aiq-select"
              value={statusBU}
              onChange={(e) => {
                setStatusBU(e.target.value);
                if (e.target.value) setStatusLeader("");
              }}
              disabled={!!statusLeader}
            >
              <option value="">— any —</option>
              {businessUnits.map((bu) => (
                <option key={bu} value={bu}>
                  {bu}
                </option>
              ))}
            </select>

            <label htmlFor="st-filter" className="aiq-label">Include</label>
            <select
              id="st-filter"
              className="aiq-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">Everyone (assessed + not assessed)</option>
              <option value="assessed">Only assessed</option>
              <option value="not_assessed">Only not assessed</option>
            </select>

            <ButtonRow>
              <button
                type="button"
                className="aiq-btn-primary"
                disabled={busy === "status"}
                onClick={() =>
                  download(
                    "status",
                    () =>
                      downloadAssessmentStatusReport({
                        leaderId: statusLeader || null,
                        businessUnit: statusBU || null,
                        status: statusFilter,
                      }),
                    `assessment-${statusFilter}.pdf`
                  )
                }
              >
                {busy === "status" ? "Generating…" : "Download PDF"}
              </button>
              <button
                type="button"
                className="aiq-btn-ghost"
                disabled={busy === "status-xlsx"}
                onClick={() =>
                  download(
                    "status-xlsx",
                    () =>
                      downloadAssessmentStatusReportExcel({
                        leaderId: statusLeader || null,
                        businessUnit: statusBU || null,
                        status: statusFilter,
                      }),
                    `assessment-${statusFilter}.xlsx`
                  )
                }
              >
                {busy === "status-xlsx" ? "Generating…" : "Download Excel"}
              </button>
            </ButtonRow>
          </Card>
        )}
      </div>
    </div>
  );
}

const Card = ({ title, children }) => (
  <div className="aiq-card">
    <h3 style={{ marginTop: 0 }}>{title}</h3>
    {children}
  </div>
);

const ButtonRow = ({ children }) => (
  <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
    {children}
  </div>
);

const smallText = { fontSize: 13, marginTop: 0, marginBottom: 8 };
